import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const allStates = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
  "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

interface WikiStateGridProps {
  variant?: "light" | "dark";
  compact?: boolean;
}

export default function WikiStateGrid({ variant = "light", compact = false }: WikiStateGridProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get current state from URL if on a state page
  const currentPath = location.pathname;
  const currentStateMatch = currentPath.match(/\/wiki\/([a-z]{2})/i);
  const currentState = currentStateMatch ? currentStateMatch[1].toUpperCase() : null;

  const handleStateClick = (stateCode: string) => {
    navigate(`/wiki/${stateCode.toLowerCase()}`);
  };

  const handleFederalClick = () => {
    navigate("/wiki/federal/foia");
  };

  const isDark = variant === "dark";
  const bgClass = isDark ? "bg-brand-dark" : "bg-white";
  const borderClass = isDark ? "border-brand-orange/20" : "border-brand-stroke";
  const textClass = isDark ? "text-white" : "text-brand-dark";
  const subtextClass = isDark ? "text-brand-gray" : "text-gray-600";

  return (
    <div className={`${bgClass} border ${borderClass} rounded-lg p-${compact ? "4" : "6"}`}>
      {!compact && (
        <p className={`font-kepler text-sm ${subtextClass} mb-4 text-center`}>
          Quick navigation to any jurisdiction
        </p>
      )}

      <div className="grid grid-cols-5 lg:grid-cols-10 gap-2 mb-4">
        {allStates.map((code) => (
          <button
            key={code}
            onMouseEnter={() => setHoveredState(code)}
            onMouseLeave={() => setHoveredState(null)}
            onClick={() => handleStateClick(code)}
            className={`px-3 py-2 rounded text-xs font-bold transition-all duration-200 ${
              currentState === code
                ? "bg-brand-orange text-white ring-2 ring-brand-orange"
                : hoveredState === code
                ? "bg-brand-azure text-white scale-105"
                : isDark
                ? "bg-brand-martinique/50 text-white hover:bg-brand-martinique"
                : "bg-gray-200 text-brand-dark hover:bg-gray-300"
            }`}
          >
            {code}
          </button>
        ))}
      </div>

      <button
        onClick={handleFederalClick}
        className={`w-full px-4 py-3 rounded-lg font-kepler font-bold transition-colors flex items-center justify-center gap-2 ${
          currentPath.includes("/wiki/federal")
            ? "bg-brand-orange text-white"
            : isDark
            ? "bg-brand-martinique/50 text-white hover:bg-brand-martinique"
            : "bg-brand-dark text-white hover:bg-brand-dark/90"
        }`}
      >
        <span>🏛️ Federal FOIA (5 U.S.C. § 552)</span>
      </button>
    </div>
  );
}
